import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Article } from "@myams/shared";
import { useAdminArticles, useUpdateAdminArticle } from "../../lib/queries";

type SortKey = "published" | "title" | "source";
type SortDir = "asc" | "desc";

const SOURCE_OPTIONS = [
  { id: "all", label: "All sources" },
  { id: "amsterdamfoodie", label: "Amsterdam Foodie" },
  { id: "culi-amsterdam", label: "Culi Amsterdam" },
  { id: "eating-europe", label: "Eating Europe" },
  { id: "vegan-amsterdam", label: "Vegan Amsterdam" },
];

function formatDate(ts: number | null | undefined): string {
  if (!ts) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Amsterdam",
    day: "numeric", month: "short", year: "numeric",
  }).format(new Date(ts * 1000));
}

export function AdminArticles() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(id);
  }, [q]);

  const [source, setSource] = useState<string>("all");
  const [showHidden, setShowHidden] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>("published");
  const [sortDir, setSortDir] = useState<SortDir>("desc");  // newest first

  const { data: articles = [], isPending } = useAdminArticles(debouncedQ);
  const updateArticle = useUpdateAdminArticle();

  const rows = useMemo(() => {
    let out = articles;
    if (!showHidden) out = out.filter((a) => !a.hidden);
    if (source !== "all") out = out.filter((a) => a.source === source);

    const cmp = (a: Article, b: Article): number => {
      switch (sortKey) {
        case "published": {
          const av = a.publishedAt ?? 0;
          const bv = b.publishedAt ?? 0;
          return av - bv;
        }
        case "title":  return (a.title ?? "").localeCompare(b.title ?? "");
        case "source": return (a.source ?? "").localeCompare(b.source ?? "");
      }
    };
    out = [...out].sort(cmp);
    if (sortDir === "desc") out.reverse();
    return out;
  }, [articles, showHidden, source, sortKey, sortDir]);

  const onHeader = (k: SortKey) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir(k === "published" ? "desc" : "asc"); }
  };

  const hiddenCount = articles.filter((a) => a.hidden).length;

  return (
    <div style={{ padding: "20px 12px 32px" }}>
      <div style={{
        position: "sticky", top: 0, zIndex: 4,
        background: "var(--ma-paper)", paddingBottom: 12, marginBottom: 6,
      }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="search title / excerpt / author"
            style={{
              flex: "1 1 240px", padding: "10px 12px",
              background: "var(--ma-cream)",
              border: "var(--ma-border) solid var(--ma-ink)",
              borderRadius: "var(--ma-card-radius)",
              fontFamily: "var(--ma-body)", fontSize: 16,
              color: "var(--ma-ink)", boxSizing: "border-box", minWidth: 0,
            }}
          />
          <select value={source} onChange={(e) => setSource(e.target.value)} style={selectStyle}>
            {SOURCE_OPTIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <label style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "8px 12px",
            background: showHidden ? "var(--ma-ink)" : "transparent",
            color: showHidden ? "var(--ma-paper)" : "var(--ma-ink)",
            border: "var(--ma-border) solid var(--ma-ink)",
            borderRadius: "var(--ma-card-radius)",
            fontFamily: "var(--ma-mono)", fontSize: 11, letterSpacing: "0.1em",
            textTransform: "uppercase", fontWeight: 600, cursor: "pointer",
            whiteSpace: "nowrap",
          }}>
            <input type="checkbox" checked={showHidden}
              onChange={(e) => setShowHidden(e.target.checked)}
              style={{ accentColor: "var(--ma-brick)" }} />
            show hidden ({hiddenCount})
          </label>
        </div>
        <div className="ma-mono" style={{ fontSize: 10, letterSpacing: "0.08em", color: "var(--ma-ink-soft)", marginTop: 8 }}>
          {rows.length} of {articles.length}{q && ` matching "${debouncedQ}"`}
        </div>
      </div>

      {isPending && (
        <div className="ma-mono" style={{ fontSize: 11, color: "var(--ma-ink-soft)", textAlign: "center", padding: 24 }}>
          loading articles…
        </div>
      )}

      <div style={{
        background: "var(--ma-cream)",
        border: "var(--ma-border) solid var(--ma-ink)",
        borderRadius: "var(--ma-card-radius)",
        overflow: "auto",
      }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "var(--ma-body)", fontSize: 13 }}>
          <thead>
            <tr style={{
              background: "var(--ma-ink)", color: "var(--ma-paper)",
              fontFamily: "var(--ma-mono)", fontSize: 10, letterSpacing: "0.12em",
              textTransform: "uppercase",
            }}>
              <Th onClick={() => onHeader("title")} active={sortKey === "title"} dir={sortDir}>title</Th>
              <Th onClick={() => onHeader("source")} active={sortKey === "source"} dir={sortDir} style={{ width: 140 }}>source</Th>
              <Th onClick={() => onHeader("published")} active={sortKey === "published"} dir={sortDir} style={{ width: 130 }}>published</Th>
              <Th style={{ width: 80, textAlign: "center" }}>visible</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <Row
                key={a.id}
                article={a}
                onEdit={() => navigate(`/admin/articles/${encodeURIComponent(a.id)}/edit`)}
                onToggleHidden={() => updateArticle.mutate({ id: a.id, hidden: !a.hidden })}
                busy={updateArticle.isPending}
              />
            ))}
            {!isPending && rows.length === 0 && (
              <tr>
                <td colSpan={4} style={{ padding: 24, textAlign: "center" }}>
                  <span className="ma-mono" style={{ fontSize: 11, letterSpacing: "0.1em", color: "var(--ma-ink-soft)" }}>
                    no articles match
                  </span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children, onClick, active, dir, style }: {
  children: React.ReactNode; onClick?: () => void; active?: boolean; dir?: SortDir; style?: React.CSSProperties;
}) {
  const sortable = !!onClick;
  return (
    <th
      onClick={onClick}
      style={{
        textAlign: "left", padding: "10px 14px",
        borderBottom: "var(--ma-border) solid var(--ma-ink)",
        cursor: sortable ? "pointer" : "default",
        userSelect: "none", whiteSpace: "nowrap",
        ...style,
      }}
    >
      {children}
      {active && sortable && <span style={{ marginLeft: 6, opacity: 0.8 }}>{dir === "asc" ? "↑" : "↓"}</span>}
    </th>
  );
}

function Row({ article, onEdit, onToggleHidden, busy }: {
  article: Article; onEdit: () => void; onToggleHidden: () => void; busy: boolean;
}) {
  const muted = article.hidden;
  return (
    <tr
      onClick={onEdit}
      style={{
        cursor: "pointer",
        borderBottom: "1px solid var(--ma-line)",
        background: muted ? "var(--ma-paper)" : "transparent",
        opacity: muted ? 0.7 : 1,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = "var(--ma-paper)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = muted ? "var(--ma-paper)" : "transparent"; }}
    >
      <td style={cellStyle}>
        <div style={{ fontWeight: 600, color: "var(--ma-ink)", display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 2, overflow: "hidden" }}>
          {article.title}
          {article.hidden && (
            <span className="ma-mono" style={{ marginLeft: 8, fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--ma-brick)" }}>hidden</span>
          )}
        </div>
        {article.author && (
          <div className="ma-mono" style={{ fontSize: 10, letterSpacing: "0.04em", color: "var(--ma-ink-soft)", marginTop: 2 }}>
            {article.author}
          </div>
        )}
      </td>
      <td className="ma-mono" style={{ ...cellStyle, fontSize: 11, letterSpacing: "0.06em", color: "var(--ma-ink-soft)" }}>
        {article.source}
      </td>
      <td className="ma-mono" style={{ ...cellStyle, fontSize: 12, color: "var(--ma-ink-soft)" }}>
        {formatDate(article.publishedAt)}
      </td>
      <td style={{ ...cellStyle, textAlign: "center" }}>
        <button
          onClick={(e) => { e.stopPropagation(); onToggleHidden(); }}
          disabled={busy}
          aria-label={article.hidden ? "Show article" : "Hide article"}
          style={{
            padding: "6px 10px",
            background: article.hidden ? "var(--ma-brick)" : "transparent",
            color: article.hidden ? "var(--ma-paper)" : "var(--ma-ink)",
            border: "var(--ma-border) solid var(--ma-ink)",
            borderRadius: "var(--ma-card-radius)",
            fontFamily: "var(--ma-mono)", fontSize: 10, letterSpacing: "0.1em",
            textTransform: "uppercase", fontWeight: 600, cursor: "pointer",
            opacity: busy ? 0.5 : 1,
          }}
        >
          {article.hidden ? "show" : "hide"}
        </button>
      </td>
    </tr>
  );
}

const cellStyle: React.CSSProperties = { padding: "10px 14px", verticalAlign: "middle" };
const selectStyle: React.CSSProperties = {
  padding: "10px 12px",
  background: "var(--ma-cream)",
  border: "var(--ma-border) solid var(--ma-ink)",
  borderRadius: "var(--ma-card-radius)",
  fontFamily: "var(--ma-mono)", fontSize: 12, letterSpacing: "0.06em",
  color: "var(--ma-ink)", cursor: "pointer",
};
